/**
 * accountAlerts.service.ts
 *
 * Typed API call wrapper for account alerts / announcements.
 *
 * `account_alerts` powers the dashboard "announcements" banner (active,
 * undismissed alerts) and the announcements archive (all alerts the user can
 * see, including ones they've closed). Administration lives under Support.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AccountAlert {
  accountAlertID: number;
  alertTitle: string;
  alertURL: string;
  alertContent: string;
  moreDetails: string;
  alertPhoto: string;
  /** Optional video (Loom/YouTube/Vimeo/self-hosted) opened in a modal. */
  videoURL: string;
  status: number;
  userLevel: number;
  startDate: string;
  stopDate: string;
  alertOrder: number;
  active: boolean;
  createDate: string;
  endDate: string;
  /** Set when the current user has dismissed ("closed") this announcement. */
  dismissedAt: string | null;
  accountID: number | string;
  /** Set when this is a broadcast to a whole master account (group-wide). */
  masterAccountID: number | null;
  /** True when this announcement shows to every practice in the group. */
  isBroadcast: boolean;

  // ── Hero card appearance (authored in the corporate dashboard) ────────────
  // Empty strings mean "use the default look"; resolveAlertHeroStyle in
  // @/lib/alertHeroStyle turns these into the actual background/button styles.
  /** Background colour, or the first stop of the gradient. */
  bgColor: string;
  /** Second gradient stop. Empty means a flat background. */
  bgColorEnd: string;
  /** Gradient direction token — see ALERT_BG_DIRECTIONS. */
  bgDirection: string;
  /** Full-bleed background photo, shown under a tinted scrim. */
  bgImage: string;
  /** Scrim strength over a background photo, 0-100; 100 hides it. '' = 90. */
  bgOverlayOpacity: string;
  /** Share of the card held at full strength before fading, 0-100. '' = 25. */
  bgOverlayCoverage: string;
  /** Overrides the automatic (contrast-derived) text colour. */
  textColor: string;
  /** Title font token from ALERT_TITLE_FONTS, applied to the whole title.
   *  Bold/italic/underline and colour are inline markup in alertTitle. */
  titleFont: string;
  /** Call-to-action label. The button only renders when this is set. */
  buttonText: string;
  /** Where the button goes; falls back to alertURL when empty. */
  buttonURL: string;
  buttonColor: string;
  buttonTextColor: string;
  /** Where the button sits in the card: auto / top / middle / bottom. */
  buttonPosition: string;
}

/** Fields accepted when creating/updating an announcement. */
export interface AccountAlertInput {
  alertTitle: string;
  alertURL?: string;
  alertContent?: string;
  moreDetails?: string;
  alertPhoto?: string;
  videoURL?: string;
  status?: number;
  userLevel?: number;
  startDate?: string;
  stopDate?: string;
  alertOrder?: number;
  /** When true (corp admins only), post to every practice in the group. */
  isBroadcast?: boolean;

  /** Hero card appearance — see AccountAlert above. */
  bgColor?: string;
  bgColorEnd?: string;
  bgDirection?: string;
  bgImage?: string;
  bgOverlayOpacity?: string;
  bgOverlayCoverage?: string;
  textColor?: string;
  titleFont?: string;
  buttonText?: string;
  buttonURL?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  buttonPosition?: string;
}

// ─── Viewer API calls ────────────────────────────────────────────────────────

/** Active (non-soft-deleted) alerts for the current account. Banner + archive. */
export function getAccountAlerts(): Promise<AccountAlert[]> {
  return apiClient
    .get<ApiResponse<AccountAlert[]>>('/account-alerts')
    .then((r) => r.data.data)
    .catch(() => []);
}

/** Close an announcement for the current user (persists across devices). */
export function dismissAccountAlert(id: number): Promise<void> {
  return apiClient.post(`/account-alerts/${id}/dismiss`).then(() => undefined);
}

/** Re-show a previously dismissed announcement for the current user. */
export function undismissAccountAlert(id: number): Promise<void> {
  return apiClient.delete(`/account-alerts/${id}/dismiss`).then(() => undefined);
}

// ─── Admin (authoring) API calls ─────────────────────────────────────────────

/** Admin list — pass includeInactive to also return soft-deleted rows. */
export function listAccountAlerts(includeInactive = false): Promise<AccountAlert[]> {
  return apiClient
    .get<ApiResponse<AccountAlert[]>>('/account-alerts', {
      params: includeInactive ? { include_inactive: 1 } : undefined,
    })
    .then((r) => r.data.data);
}

export function createAccountAlert(payload: AccountAlertInput): Promise<AccountAlert> {
  return apiClient
    .post<ApiResponse<AccountAlert>>('/account-alerts', payload)
    .then((r) => r.data.data);
}

export function updateAccountAlert(
  id: number,
  payload: Partial<AccountAlertInput>,
): Promise<AccountAlert> {
  return apiClient
    .put<ApiResponse<AccountAlert>>(`/account-alerts/${id}`, payload)
    .then((r) => r.data.data);
}

export function deleteAccountAlert(id: number): Promise<void> {
  return apiClient.delete(`/account-alerts/${id}`).then(() => undefined);
}

export function restoreAccountAlert(id: number): Promise<AccountAlert> {
  return apiClient
    .post<ApiResponse<AccountAlert>>(`/account-alerts/${id}/restore`)
    .then((r) => r.data.data);
}
